import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  createSharedDriveAsAdmin,
  createSharedDriveAsUser,
  updateSharedDriveAcls,
  UserKeys,
} from '../../../../../../api/users-api';
import { SharedDriveModel } from '../../../../../../../types';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import SharedDialog from '../../../../../../shared/shared-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { useStore } from '../../../../../state/zustand-store';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import { Messages } from '../../../../../../utils/message-constants';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { pagesConfig } from '../../../../../../shared/pages-config';
import SharedDriveMembersEditor from './shared-drive-members-editor';
import {
  getSharedDriveAnalyticsEvent,
  getSharedDrivePage,
  SharedDriveEvent,
} from './shared-drive-analytics';
import {
  getErrorReason,
  SHARED_DRIVE_MIME_TYPE,
  SharedDriveMembersFormValues,
} from './shared-drive-members';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
interface CreateSharedDriveResult {
  drive: SharedDriveModel;
  membersSent: boolean;
  membersQueued?: boolean;
  membersError?: unknown;
}

interface SharedDrivesCreateDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  driveRoles: string[];
}

export default function SharedDrivesCreateDialog({
  open,
  handleClose,
  changeLogRoles,
  driveRoles,
}: SharedDrivesCreateDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const userEmailFromSearchParams = searchParams.get('userEmail');
  const { userEmail: userEmailFromPathParams } = useParams() as {
    userEmail: string | undefined;
  };
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [name, setName] = useState('');
  const { setSnackbar } = useStore();
  const { currentTier, adminEmail } = useUserSettings();
  const queryClient = useQueryClient();

  const url = location.pathname.toLowerCase();
  const isSharedDrives = url.includes(pagesConfig.sharedDrives.link);
  const isDriveExplorer = url.includes(pagesConfig.driveExplorer.link);

  // acting as the selected user, and not the logged in user
  const userEmail = isDriveExplorer
    ? userEmailFromSearchParams
    : userEmailFromPathParams;
  const actingAsEmail = isSharedDrives ? adminEmail : userEmail || '';

  const isPullingFromIndexedDomainSharedDrives =
    currentTier === 'enterprise' && isSharedDrives;

  const canAddMembers = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.CanUpdateSharedDrivePermissions.value
  );

  const form = useForm<SharedDriveMembersFormValues>({
    defaultValues: { aclResources: [] },
  });
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'aclResources',
  });
  const hasMembers = canAddMembers && fieldArray.fields.length > 0;

  function track(event: SharedDriveEvent) {
    const { name, properties } = getSharedDriveAnalyticsEvent(
      event,
      getSharedDrivePage(isSharedDrives, isDriveExplorer)
    );
    createEvent(name, properties);
  }

  // Create first, then add members to the returned drive id. Both steps run in one
  // promise so the second call never starts early and still finishes if the dialog unmounts.
  const { mutate, isPending } = useMutation({
    mutationFn: async (): Promise<CreateSharedDriveResult> => {
      const driveName = name.trim();
      const members = canAddMembers ? form.getValues('aclResources') : [];

      const drive = isSharedDrives
        ? await createSharedDriveAsAdmin(driveName, changeLogMessage)
        : await createSharedDriveAsUser(
            driveName,
            userEmail || '',
            changeLogMessage
          );

      if (members.length === 0) {
        return { drive, membersSent: false };
      }

      const driveId = drive?.id;
      if (!driveId) {
        return {
          drive,
          membersSent: false,
          membersError: new Error('The new shared drive ID was not returned.'),
        };
      }

      try {
        const queued = await updateSharedDriveAcls(
          driveId,
          actingAsEmail,
          SHARED_DRIVE_MIME_TYPE,
          members.map((member) => ({
            ...member,
            docId: driveId,
            docOwner: actingAsEmail,
            docTitle: driveName,
          })),
          driveName,
          changeLogMessage
        );
        return { drive, membersSent: true, membersQueued: queued };
      } catch (membersError) {
        return { drive, membersSent: true, membersError };
      }
    },
    onSuccess: ({ membersSent, membersQueued, membersError }) => {
      if (membersError) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: Messages.SharedDrives.CreatedMembersFailed(
            getErrorReason(membersError)
          ),
        });
      } else if (membersQueued) {
        setSnackbar({
          open: true,
          severity: 'info',
          message: Messages.SharedDrives.CreatedMembersTaskStarted,
        });
      } else if (membersSent) {
        setSnackbar({
          open: true,
          severity: 'success',
          message: isPullingFromIndexedDomainSharedDrives
            ? Messages.SharedDrives.CreatedWithMembers
            : Messages.SharedDrives.CreatingWithMembers,
        });
      } else {
        setSnackbar({
          open: true,
          severity: isPullingFromIndexedDomainSharedDrives ? 'success' : 'info',
          message: isPullingFromIndexedDomainSharedDrives
            ? Messages.Success.Created('Shared drive')
            : `Shared drive is currently being created!`,
        });
      }

      if (membersSent) {
        track('update_drive_shares');
      }

      if (isPullingFromIndexedDomainSharedDrives) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDrivesIndexedPage],
        });
      }
      if (!isSharedDrives && userEmail) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDrivesForUser, userEmail],
        });
      }
      if (membersSent) {
        queryClient.invalidateQueries({ queryKey: [UserKeys.DriveAcls] });
      }

      setChangeLogMessage('');
      form.reset();
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating('the shared drive'),
      });
    },
    onSettled: () => {
      track('create_drive');
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    const requiresChangeLog =
      isAbleToSeeUIElement(
        changeLogRoles,
        DriveRoles.CanCreateSharedDrives.value
      ) ||
      (hasMembers &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DriveRoles.CanUpdateSharedDrivePermissions.value
        ));

    if (requiresChangeLog) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  function handleDialogClose() {
    if (isPending) return;
    handleClose();
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleDialogClose}
      title={`Create Shared Drive`}
      actingAs={actingAsEmail}
      dialogContent={
        <>
          {!isPullingFromIndexedDomainSharedDrives ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Newly created shared drives will not be immediately shown in the
              grid. This can take 1-2 minutes within Google and refreshing the
              grid after that time will show the new shared drive.
            </Typography>
          ) : null}
          <TextField
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            label="Shared Drive Name"
          />
          {canAddMembers ? (
            <Stack sx={{ mt: 3 }} gap={1}>
              <Typography>Add members</Typography>
              <SharedDriveMembersEditor
                form={form}
                fieldArray={fieldArray}
                originalMembers={[]}
                newMemberDefaults={{
                  docId: '',
                  docOwner: actingAsEmail,
                  docTitle: name.trim(),
                }}
                listHeader={
                  hasMembers ? (
                    <Typography variant="body2">People with access</Typography>
                  ) : null
                }
              />
            </Stack>
          ) : null}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => {
              if (!isPending) setOpenChangeLogDialog(false);
            }}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: 'Create Shared Drive',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending || !name.trim(),
      }}
    />
  );
}
